"use client";

/**
 * DeclineOrderSheet — destructive decline confirmation as a responsive
 * bottom-sheet (mobile) / centered card (desktop).
 *
 * Structure mirrors CancelOrderSheet exactly, adapted for the decline flow:
 * - Two decline reason chips (amount_not_received, amount_not_match)
 * - No refund clause
 * - Red "Decline order" CTA
 */

import { useState } from "react";
import { ModalSheet } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { DeclineReasonCode } from "@/lib/api/types";

/* ── Types ─────────────────────────────────────────────────────────────── */

export type DeclineOrderSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reasonCode: DeclineReasonCode, note?: string) => void;
  isSubmitting: boolean;
};

/* ── Reason chips config ────────────────────────────────────────────────── */

const DECLINE_CHIPS: ReadonlyArray<{
  code: DeclineReasonCode;
  labelKey: string;
}> = [
  { code: "amount_not_received", labelKey: keys.orders.declineAmountNotReceived },
  { code: "amount_not_match", labelKey: keys.orders.declineAmountNotMatch },
];

/* ── Component ──────────────────────────────────────────────────────────── */

export function DeclineOrderSheet({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}: DeclineOrderSheetProps) {
  const t = useTranslations();

  // Default to the first chip pre-selected.
  const [selectedCode, setSelectedCode] =
    useState<DeclineReasonCode>("amount_not_received");

  const handleConfirm = () => {
    if (isSubmitting) return;
    onConfirm(selectedCode);
  };

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="md" elevated>
      {/* Scrollable inner area */}
      <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-5 pb-5 pt-4">

        {/* Warning circle icon */}
        <div className="flex justify-center">
          <div
            className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px]"
            style={{ backgroundColor: "#fde7d3" }}
          >
            <XCircleIcon />
          </div>
        </div>

        {/* Title */}
        <div className="text-center">
          <h2
            className="text-[18px] font-bold leading-snug"
            style={{ color: "#13171f" }}
          >
            {t(keys.orders.declineSheetTitle)}
          </h2>
        </div>

        {/* Body copy */}
        <p
          className="text-center text-[13px] leading-relaxed"
          style={{ color: "#6f6a5e" }}
        >
          {t(keys.orders.declineSheetBody)}
        </p>

        {/* Reason section */}
        <div className="flex flex-col gap-2.5">
          {/* Eyebrow */}
          <span
            className="text-[11px] font-bold uppercase tracking-[0.06em]"
            style={{ color: "#9a9486" }}
          >
            {t(keys.orders.cancelReasonEyebrow)}
          </span>

          {/* Chips */}
          <div className="flex flex-wrap gap-2">
            {DECLINE_CHIPS.map(({ code, labelKey }) => {
              const isSelected = selectedCode === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => setSelectedCode(code)}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors"
                  style={
                    isSelected
                      ? { backgroundColor: "#13171f", color: "#ffffff", border: "1px solid #13171f" }
                      : { backgroundColor: "#ffffff", color: "#3a3f4a", border: "1px solid #e3ddd0" }
                  }
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2.5">
          {/* Primary destructive button */}
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleConfirm}
            className="h-[50px] w-full rounded-[13px] text-[15px] font-bold text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: "#dc2626" }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#b91c1c";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#dc2626";
            }}
          >
            {isSubmitting
              ? t(keys.orders.savingAction)
              : t(keys.orders.declineConfirm)}
          </button>

          {/* Secondary ghost button */}
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-[50px] w-full rounded-[13px] border text-[15px] font-medium transition-colors disabled:opacity-60"
            style={{
              backgroundColor: "#ffffff",
              borderColor: "#e3ddd0",
              color: "#13171f",
            }}
          >
            {t(keys.orders.keepOrder)}
          </button>
        </div>
      </div>
    </ModalSheet>
  );
}

/* ── X circle icon ──────────────────────────────────────────────────────── */

function XCircleIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#dc2626"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}
